import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/adminApi';

const envApiBase = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL || '';
const normalizeBase = (base) => (base || '').replace(/\/$/, '');
const buildSearchEndpoint = (base) => {
  const b = normalizeBase(base);
  return b ? `${b}/api/carrier_search.php` : '/api/carrier_search.php';
};

const SEARCH_API = (() => {
  if (typeof window === 'undefined') return buildSearchEndpoint(envApiBase);
  const base = envApiBase || window.location.origin || '';
  return buildSearchEndpoint(base);
})();

// ─── Utilities ───────────────────────────────────────────────────────────────
const tidy = (s) => (s || '').toString().trim();
const digits = (s) => (s || '').toString().replace(/\D/g, '');
const stripLeadingZeros = (s) => (s || '').toString().replace(/^0+/, '');
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const normalizeOperationClassification = (classification, authorityInfo) => {
  const base = tidy(classification) || 'N/A';
  if (!authorityInfo?.commonAuthority) return base;

  const upper = base.toUpperCase();
  const commonAuthority = authorityInfo.commonAuthority;

  if (upper === 'AUTHORIZED FOR HIRE' && commonAuthority !== 'Active') {
    return 'NOT AUTHORIZED FOR HIRE';
  }

  return base;
};

async function postToAPI(action, body = {}, authToken = '') {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
    headers['X-Auth-Token'] = authToken;
  }

  const res = await fetch(SEARCH_API, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({ action, ...body }),
  });

  if (!res.ok) {
    let msg = `API Error: ${res.status}`;
    try {
      const j = await res.json();
      if (j && j.error) msg = j.error;
    } catch {/* ignore */}
    throw new Error(msg);
  }

  return res.json();
}

// ─── Carrier API Service ─────────────────────────────────────────────────────
// All query building and dataset selection happens on the PHP backend.
// This class only sends abstract requests: { action, type, term, filters }
class CarrierAPIService {
  constructor(getToken) {
    this._getToken = getToken || (() => '');
  }

  get _token() {
    return typeof this._getToken === 'function' ? this._getToken() : '';
  }

  detectSearchType(input) {
    const trimmed = tidy(input);
    const numeric = digits(trimmed);
    if (/^MC-?\d{6,8}$/i.test(trimmed) || /^\d{6,8}$/.test(trimmed)) return 'mc';
    if (/^\d{7,8}$/.test(trimmed)) return 'dot';
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'email';
    if ((numeric.length >= 7 && numeric.length <= 15) && /[\d\s()+.-]+/.test(trimmed)) return 'phone';
    if (/^\d+\s*-\s*\d+$/.test(trimmed)) return 'range';
    if (/^[a-zA-Z\s.,&]+$/.test(trimmed) && trimmed.length > 2) return 'name';
    return 'auto';
  }

  determineSearchType(text) {
    const trimmedText = tidy(text).toLowerCase();
    const mcRegex = /\b(?:mc|motor carrier|docket|authority)\s*[-:#]?\s*(\d+)/i;
    const dotRegex = /\b(?:dot|usdot)(?:\s*[-:#]?\s*|\s+number\s*[-:#]?\s*)(\d+)/i;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
    const rangeRegex = /^(\d+)\s*-\s*(\d+)$/;
    const phoneDigits = digits(trimmedText);

    const rangeMatch = trimmedText.match(rangeRegex);
    if (rangeMatch) return { type: 'range', start: rangeMatch[1], end: rangeMatch[2] };
    if (emailRegex.test(trimmedText)) return { type: 'email', value: trimmedText };
    if (phoneDigits.length >= 7 && phoneDigits.length <= 15 && /[\d\s()+.-]+/.test(trimmedText)) {
      return { type: 'phone', value: phoneDigits };
    }

    const mcMatch = trimmedText.match(mcRegex);
    if (mcMatch) return { type: 'mc', value: mcMatch[1] };

    const dotMatch = trimmedText.match(dotRegex);
    if (dotMatch) return { type: 'dot', value: dotMatch[1] };

    const numberMatch = trimmedText.match(/^\d+$/);
    if (numberMatch) {
      return {
        type: numberMatch[0].length <= 7 ? 'mc' : 'dot',
        value: numberMatch[0]
      };
    }

    return { type: 'auto', value: trimmedText };
  }

  // ── Search: sends abstract request to backend ──────────────────────────────
  async search(term, searchType = 'auto', filters = {}) {
    const searchConfig = searchType === 'auto'
      ? this.determineSearchType(term)
      : { type: searchType, value: term };

    // Range search — batch individual lookups
    if (searchConfig.type === 'range') {
      if (!searchConfig.start || !searchConfig.end) {
        throw new Error('Please enter both start and end numbers for range search');
      }
      const start = parseInt(searchConfig.start, 10);
      const end = parseInt(searchConfig.end, 10);
      if (Number.isNaN(start) || Number.isNaN(end)) throw new Error('Please enter valid numbers for range search');
      if (start > end) throw new Error('Start number cannot be greater than end number');
      const rangeResults = await this.searchRange(start, end);
      return this.applyFilters(rangeResults, filters);
    }

    if (!tidy(term)) throw new Error('Please enter a search term');

    // ── Backend handles ALL query building — we only send type + term + filters
    const data = await postToAPI('search', {
      type: searchConfig.type,
      term: searchConfig.value || tidy(term),
      filters: {
        states: Array.isArray(filters.states) ? filters.states : [],
        resultLimit: filters.resultLimit || 0,
      },
    }, this._token);

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('No carrier found with the provided search criteria');
    }

    const processedData = data.map(item => this.processBasicCarrierData(item));
    const enrichedData = await this.enrichCarrierCollection(processedData);
    return this.applyFilters(enrichedData, filters);
  }

  async searchRange(start, end) {
    const validResults = [];

    // Process in small chunks so large ranges do not exhaust the browser with thousands of promises.
    const concurrency = 5;
    for (let batchStart = start; batchStart <= end; batchStart += concurrency) {
      const batchEnd = Math.min(end, batchStart + concurrency - 1);
      const batchPromises = [];

      for (let current = batchStart; current <= batchEnd; current++) {
        const delay = (current - batchStart) * 100;
        batchPromises.push(
          new Promise(resolve => setTimeout(resolve, delay))
            .then(() => this.searchSingleMC(current.toString()))
        );
      }

      const batchResults = await Promise.allSettled(batchPromises);
      validResults.push(
        ...batchResults
          .filter(r => r.status === 'fulfilled' && r.value !== null)
          .map(r => r.value)
      );
    }

    if (!validResults.length) throw new Error('No carriers found in the specified range');
    return validResults;
  }

  async searchSingleMC(mcNumber) {
    try {
      const strippedMC = stripLeadingZeros(digits(mcNumber));
      // Abstract single-carrier lookup — NO dataset ID sent
      const data = await postToAPI('search_single', {
        type: 'mc',
        term: strippedMC,
      }, this._token);

      if (!Array.isArray(data) || data.length === 0) return null;
      const basicData = this.processBasicCarrierData(data[0]);
      return this.enrichCarrierRecord(basicData);
    } catch {
      return null;
    }
  }

  async enrichCarrierRecord(basicData) {
    if (!basicData || basicData.mcNumber === 'N/A') {
      return basicData;
    }

    try {
      const additionalData = await this.getAdditionalCarrierInfo(basicData.mcNumber);
      const fullData = { ...basicData, ...additionalData };
      fullData.entityClassification = normalizeOperationClassification(
        fullData.entityClassification,
        fullData.authorityInfo
      );
      return fullData;
    } catch {
      return basicData;
    }
  }

  async enrichCarrierCollection(carriers = []) {
    if (!Array.isArray(carriers) || carriers.length === 0) return [];

    const concurrency = 4;
    const enriched = [];

    for (let i = 0; i < carriers.length; i += concurrency) {
      const batch = carriers.slice(i, i + concurrency);
      const batchResults = await Promise.all(batch.map((carrier) => this.enrichCarrierRecord(carrier)));
      enriched.push(...batchResults);
    }

    return enriched;
  }

  applyFilters(data, filters = {}) {
    if (!data) return null;
    let filteredData = Array.isArray(data) ? data : [data];
    const hasValue = (value) => value && value !== 'N/A' && value !== '0';
    const numberValue = (value) => {
      const parsed = parseInt((value || '').toString().replace(/[^\d]/g, ''), 10);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    if (filters.skipBrokers) {
      filteredData = filteredData.filter(carrier =>
        carrier.entityType && !carrier.entityType.includes('Broker')
      );
    }
    if (filters.skipInactive) {
      filteredData = filteredData.filter(carrier => carrier.status === 'Active');
    }

    const stArr = Array.isArray(filters.states) ? filters.states.map(s => s.toUpperCase()) : [];
    if (stArr.length) {
      filteredData = filteredData.filter(c => {
        const p = (c.phyState || '').toUpperCase();
        const m = (c.mailState || '').toUpperCase();
        return stArr.includes(p) || stArr.includes(m) ||
          (c.physicalAddress && stArr.some(st => c.physicalAddress.includes(`, ${st} `))) ||
          (c.mailingAddress && stArr.some(st => c.mailingAddress.includes(`, ${st} `)));
      });
    }

    if (filters.showInactiveOnly) {
      filteredData = filteredData.filter(carrier => carrier.status !== 'Active');
    }

    if (filters.requireEmail) {
      filteredData = filteredData.filter(carrier => hasValue(carrier.email));
    }

    if (filters.requirePhone) {
      filteredData = filteredData.filter(carrier => hasValue(carrier.phone) || hasValue(carrier.cellPhone));
    }

    if (filters.requireMC) {
      filteredData = filteredData.filter(carrier => hasValue(carrier.mcNumber));
    }

    if (filters.requireCommonAuthority) {
      filteredData = filteredData.filter(carrier => carrier.authorityInfo?.commonAuthority === 'Active');
    }

    if (filters.businessType && filters.businessType !== 'any') {
      filteredData = filteredData.filter(carrier => {
        const businessType = (carrier.businessOrganization || '').toLowerCase();
        return businessType === filters.businessType.toLowerCase();
      });
    }

    if (filters.operationScope && filters.operationScope !== 'any') {
      filteredData = filteredData.filter(carrier => {
        const operation = (carrier.carrierOperation || '').toLowerCase();
        if (filters.operationScope === 'interstate') return operation.includes('interstate');
        if (filters.operationScope === 'intrastate') return operation.includes('intrastate');
        return true;
      });
    }

    if (filters.authorityState && filters.authorityState !== 'any') {
      filteredData = filteredData.filter(carrier => {
        const authority = carrier.authorityInfo?.commonAuthority || 'N/A';
        if (filters.authorityState === 'active') return authority === 'Active';
        if (filters.authorityState === 'inactive') return authority !== 'Active';
        return true;
      });
    }

    if (filters.cargoKeyword && filters.cargoKeyword.trim()) {
      const cargoTerm = filters.cargoKeyword.trim().toLowerCase();
      filteredData = filteredData.filter(carrier =>
        (carrier.cargoClassification || '').toLowerCase().includes(cargoTerm)
      );
    }

    if (filters.pendingInsuranceOnly) {
      filteredData = filteredData.filter(carrier => Boolean(carrier.pendingInsurance));
    }

    if (filters.revocationOnly) {
      filteredData = filteredData.filter(carrier =>
        Boolean(
          carrier.authorityInfo?.revocationCommon ||
          carrier.authorityInfo?.revocationContract ||
          carrier.authorityInfo?.revocationBroker ||
          carrier.revocationInfo?.hasRevocation
        )
      );
    }

    if (filters.activeDuration > 0) {
      filteredData = filteredData.filter(carrier => {
        if (!carrier.activeDuration || carrier.activeDuration === 'N/A') return false;
        const monthsMatch = carrier.activeDuration.match(/(\d+)\s*months?/);
        const yearsMatch = carrier.activeDuration.match(/(\d+)\s*years?/);
        let totalMonths = 0;
        if (yearsMatch) totalMonths += parseInt(yearsMatch[1], 10) * 12;
        if (monthsMatch) totalMonths += parseInt(monthsMatch[1], 10);
        return totalMonths >= filters.activeDuration;
      });
    }

    if ((filters.minPowerUnits || 0) > 0) {
      filteredData = filteredData.filter(carrier => numberValue(carrier.powerUnits) >= filters.minPowerUnits);
    }

    if ((filters.minDrivers || 0) > 0) {
      filteredData = filteredData.filter(carrier => numberValue(carrier.totalDrivers) >= filters.minDrivers);
    }

    if ((filters.minCDLDrivers || 0) > 0) {
      filteredData = filteredData.filter(carrier => numberValue(carrier.totalCDLDrivers) >= filters.minCDLDrivers);
    }

    if ((filters.minCoverageAmount || 0) > 0) {
      filteredData = filteredData.filter(carrier => numberValue(carrier.insuranceInfo?.maxCoverage) >= filters.minCoverageAmount);
    }

    if (filters.resultLimit && filteredData.length > filters.resultLimit) {
      filteredData = filteredData.slice(0, filters.resultLimit);
    }

    return filteredData.length === 1 ? filteredData[0] : filteredData;
  }

  // ── Get Additional Info: ONE abstract request, backend fetches 5 sources ──
  async getAdditionalCarrierInfo(mcNumber) {
    const mc = mcNumber.toString().replace(/\D/g, '').replace(/^0+/, '');
    const results = await postToAPI('get_additional', {
      term: mc ? `MC${mc}` : mcNumber,
    }, this._token);

    if (!results || typeof results !== 'object') return {};

    const additionalData = {};

    if (results.carrierInfo) {
      const ciArr = Array.isArray(results.carrierInfo) ? results.carrierInfo : [results.carrierInfo];
      if (ciArr.length > 0 && ciArr[0]) {
        additionalData.authorityInfo = this.processAuthorityData(ciArr[0]);
      }
    }
    if (results.insuranceInfo) {
      const iiArr = Array.isArray(results.insuranceInfo) ? results.insuranceInfo : [results.insuranceInfo];
      if (iiArr.length > 0 && iiArr[0]) {
        additionalData.insuranceInfo = this.processInsuranceData(iiArr[0]);
      }
    }
    if (results.pendingInsurance) {
      const piArr = Array.isArray(results.pendingInsurance) ? results.pendingInsurance : [results.pendingInsurance];
      if (piArr.length > 0 && piArr[0]) {
        additionalData.pendingInsurance = this.processPendingInsuranceData(piArr[0]);
      }
    }
    if (results.authorityHistory) {
      const ahArr = Array.isArray(results.authorityHistory) ? results.authorityHistory : [results.authorityHistory];
      if (ahArr.length > 0 && ahArr[0]) {
        additionalData.authorityHistory = this.processAuthorityHistory(ahArr);
        additionalData.activeDuration = this.calculateActiveDuration(ahArr);
      }
    }
    if (results.revocations) {
      const rvArr = Array.isArray(results.revocations) ? results.revocations : [results.revocations];
      if (rvArr.length > 0 && rvArr[0]) {
        additionalData.revocationInfo = this.processRevocationData(rvArr[0]);
      }
    }

    additionalData.alerts = this.collectAlerts([
      { title: 'Carrier Info', data: results.carrierInfo },
      { title: 'Pending Insurance Actions', data: results.pendingInsurance },
      { title: 'Revocations', data: results.revocations },
    ]);

    return additionalData;
  }

  // ─── Data Processing (just formats raw fields — no API details) ────────────
  processBasicCarrierData(carrier) {
    const carshipMap = {
      'C': 'Carrier', 'S': 'Shipper', 'B': 'Broker', 'R': 'Registrant',
      'F': 'Freight Forwarder', 'I': 'Intermodal Equipment Provider', 'T': 'Cargo Tank'
    };

    const cargoClassifications = {
      'crgo_genfreight': 'General Freight', 'crgo_household': 'Household Goods',
      'crgo_metalsheet': 'Metal: Sheets, Coils, Rolls', 'crgo_motoveh': 'Motor Vehicles',
      'crgo_drivetow': 'Driveaway/Towaway', 'crgo_logpole': 'Logs, Poles, Beams, Lumber',
      'crgo_bldgmat': 'Building Materials', 'crgo_mobilehome': 'Mobile Homes',
      'crgo_machlrg': 'Machinery, Large Objects', 'crgo_produce': 'Fresh Produce',
      'crgo_liqgas': 'Liquids/Gases', 'crgo_intermodal': 'Intermodal Containers',
      'crgo_passengers': 'Passengers', 'crgo_oilfield': 'Oilfield Equipment',
      'crgo_livestock': 'Livestock', 'crgo_grainfeed': 'Grain, Feed, Hay',
      'crgo_coalcoke': 'Coal, Coke', 'crgo_meat': 'Meat',
      'crgo_garbage': 'Garbage, Refuse, Trash', 'crgo_usmail': 'U.S. Mail',
      'crgo_chem': 'Chemicals', 'crgo_drybulk': 'Commodities Dry Bulk',
      'crgo_coldfood': 'Refrigerated Food', 'crgo_beverages': 'Beverages',
      'crgo_paperprod': 'Paper Products', 'crgo_utility': 'Utility',
      'crgo_farmsupp': 'Farm Supplies', 'crgo_construct': 'Construction',
      'crgo_waterwell': 'Water - Well', 'crgo_cargoothr': 'Other'
    };

    const cargoTypes = Object.keys(cargoClassifications)
      .filter(key => carrier[key] === 'X')
      .map(key => cargoClassifications[key]);

    const statusMap = { 'A': 'Active', 'I': 'Inactive' };
    const carrierOperationMap = {
      'A': 'Interstate', 'B': 'Intrastate Hazardous Material', 'C': 'Intrastate Non-Hazardous Material'
    };
    const businessOrgMap = { '1': 'Individual', '2': 'Partnership', '3': 'Corporation' };

    const entityTypes = carrier.carship
      ? carrier.carship.split(';').map(type => carshipMap[type] || 'Unknown').join(', ')
      : 'N/A';

    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6) - 1;
      const day = dateStr.substring(6, 8);
      return new Date(year, month, day).toLocaleDateString('en-US');
    };

    return {
      legalName: carrier.legal_name || 'N/A',
      dbaName: carrier.dba_name || 'N/A',
      dotNumber: carrier.dot_number || 'N/A',
      mcNumber: carrier.docket1 || 'N/A',
      phyState: carrier.phy_state || 'N/A',
      mailState: carrier.carrier_mailing_state || 'N/A',
      entityType: entityTypes,
      entityClassification: normalizeOperationClassification(carrier.classdef || 'N/A', null),
      status: statusMap[carrier.status_code] || 'N/A',
      carrierOperation: carrierOperationMap[carrier.carrier_operation] || 'N/A',
      businessOrganization: businessOrgMap[carrier.business_org_id] || 'N/A',
      cargoClassification: cargoTypes.length > 0 ? cargoTypes.join(', ') : 'N/A',
      mcs150Date: formatDate(carrier.mcs150_date),
      addDate: formatDate(carrier.add_date),
      mcs150Mileage: `${carrier.mcs150_mileage || 'N/A'} miles`,
      mcs150MileageYear: carrier.mcs150_mileage_year || 'N/A',
      companyOfficer1: carrier.company_officer_1 || 'N/A',
      companyOfficer2: carrier.company_officer_2 || 'N/A',
      phone: carrier.phone || 'N/A',
      fax: carrier.fax || 'N/A',
      cellPhone: carrier.cell_phone || 'N/A',
      email: carrier.email_address || 'N/A',
      physicalAddress: `${carrier.phy_street || 'N/A'}, ${carrier.phy_city || 'N/A'}, ${carrier.phy_state || 'N/A'} ${carrier.phy_zip || 'N/A'}`,
      mailingAddress: `${carrier.carrier_mailing_street || 'N/A'}, ${carrier.carrier_mailing_city || 'N/A'}, ${carrier.carrier_mailing_state || 'N/A'} ${carrier.carrier_mailing_zip || 'N/A'}`,
      totalCDLDrivers: carrier.total_cdl || '0',
      totalDrivers: carrier.total_drivers || '0',
      powerUnits: carrier.power_units || '0',
      ownTrucks: carrier.owntruck || '0',
      ownTractors: carrier.owntract || '0',
      ownTrailers: carrier.owntrail || '0',
      termLeasedTrucks: carrier.trmtruck || '0',
      termLeasedTractors: carrier.trmtract || '0',
      termLeasedTrailers: carrier.trmtrail || '0',
      authorityInfo: null,
      insuranceInfo: null,
      pendingInsurance: null,
      authorityHistory: null,
      revocationInfo: null,
      activeDuration: null,
      alerts: []
    };
  }

  processAuthorityData(data) {
    if (!data) return null;
    return {
      commonAuthority: data.common_stat === 'A' ? 'Active' : data.common_stat === 'I' ? 'Inactive' : 'No Authority',
      brokerAuthority: data.broker_stat === 'A' ? 'Active' : data.broker_stat === 'I' ? 'Inactive' : 'No Authority',
      pendingCommon: data.common_app_pend === 'Y',
      pendingContract: data.contract_app_pend === 'Y',
      pendingBroker: data.broker_app_pend === 'Y',
      revocationCommon: data.common_rev_pend === 'Y',
      revocationContract: data.contract_rev_pend === 'Y',
      revocationBroker: data.broker_rev_pend === 'Y'
    };
  }

  processInsuranceData(data) {
    if (!data) return null;
    const insTypeMapping = {
      "1.0": "BI&PD", "2.0": "Cargo", "3.0": "Bond", "4.0": "Trust Fund"
    };
    const insClassMapping = {
      "P": "Primary", "E": "Excess",
      "1": "Full Security Limits Under Section 1043.2(b)(1)",
      "2": "Full Security Limits Under Section 1043.2(b)(2)"
    };
    const insFormMapping = {
      "34": "Cargo", "82": "BI&PD", "83": "Cargo", "84": "Property Broker's Surety Bond",
      "85": "Property Broker's Trust Fund Agreement", "91": "BI&PD/Primary, BI&PD",
      "91X": "BI&PD, BI&PD/Primary, BI&PD/Excess"
    };

    return {
      type: insTypeMapping[data.ins_type_code] || 'Unknown',
      class: insClassMapping[data.ins_class_code] || 'Unknown',
      maxCoverage: data.max_cov_amount ? `$${(parseFloat(data.max_cov_amount) * 1000).toLocaleString()}` : 'N/A',
      underlyingLimit: data.underl_lim_amount ? `$${(parseFloat(data.underl_lim_amount) * 1000).toLocaleString()}` : 'N/A',
      policyNumber: data.policy_no || 'N/A',
      effectiveDate: data.effective_date || 'N/A',
      formCode: insFormMapping[data.ins_form_code] || 'Unknown',
      companyName: data.name_company || 'N/A'
    };
  }

  processPendingInsuranceData(data) {
    if (!data || !data.cancl_effective_date) return null;
    return {
      formCode: data.ins_form_code || 'N/A',
      typeDescription: data.mod_col_1 || 'N/A',
      companyName: data.name_company || 'N/A',
      policyNumber: data.policy_no || 'N/A',
      postedDate: data.trans_date || 'N/A',
      underlyingLimit: data.underl_lim_amount ? `$${(parseFloat(data.underl_lim_amount) * 1000).toLocaleString()}` : 'N/A',
      maxCoverage: data.max_cov_amount ? `$${(parseFloat(data.max_cov_amount) * 1000).toLocaleString()}` : 'N/A',
      effectiveDate: data.effective_date || 'N/A',
      cancelEffectiveDate: data.cancl_effective_date || 'N/A'
    };
  }

  processAuthorityHistory(data) {
    if (!data) return null;
    const entries = Array.isArray(data) ? data : [data];
    const latestEntry = entries.find(item =>
      item && (item.disp_served_date || item.orig_served_date || item.original_action_desc || item.mod_col_1)
    );
    if (!latestEntry) return null;
    return {
      action: latestEntry.original_action_desc || 'N/A',
      serviceType: latestEntry.mod_col_1 || 'N/A',
      servedDate: latestEntry.disp_served_date || latestEntry.orig_served_date || 'N/A'
    };
  }

  processRevocationData(data) {
    if (!data) return null;
    return { hasRevocation: data.common_rev_pend === 'Y' };
  }

  calculateActiveDuration(data) {
    if (!data) return 'N/A';
    try {
      const grantedEntry = Array.isArray(data)
        ? data.find(event => event.mod_col_1 === "MOTOR PROPERTY COMMON CARRIER" && event.original_action_desc === "GRANTED")
        : data;
      if (grantedEntry) {
        const grantedDate = new Date(grantedEntry.disp_served_date || grantedEntry.orig_served_date);
        const currentDate = new Date();
        const duration = currentDate - grantedDate;
        const years = Math.floor(duration / (1000 * 60 * 60 * 24 * 365));
        const months = Math.floor((duration % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
        if (years === 0) return `${months} months`;
        if (months === 0) return `${years} years`;
        return `${years} years, ${months} months`;
      }
    } catch {/* ignore */}
    return 'N/A';
  }

  collectAlerts(results) {
    const alerts = [];
    (results || []).forEach(({ title, data }) => {
      const items = Array.isArray(data) ? data : (data ? [data] : []);
      if (items.length > 0) {
        const item = items[0];
        if (item.common_rev_pend === 'Y') alerts.push("Common Authority In Revocation");
        if (title === 'Pending Insurance Actions' && item.cancl_effective_date) {
          alerts.push(`Pending insurance cancellation effective: ${item.cancl_effective_date}`);
        }
        if (title === 'Revocations' && item.common_rev_pend === 'Y') {
          alerts.push('Revocation order found');
        }
      }
    });
    return alerts;
  }
}

// ─── React Hook ──────────────────────────────────────────────────────────────

export const useCarrierData = ({ allowGuestSearch = false } = {}) => {
  const { isAuthenticated, recordSearch, usageRemaining, token } = useAuth();
  const [carrierData, setCarrierData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBasicInfo, setShowBasicInfo] = useState(true);
  const [isBatchPaused, setIsBatchPaused] = useState(false);
  const [isBatchSearching, setIsBatchSearching] = useState(false);

  // Stable ref for token so apiService always has current token
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const apiServiceRef = useRef(new CarrierAPIService(() => tokenRef.current || ''));
  const apiService = apiServiceRef.current;
  const batchPausedRef = useRef(false);

  const waitIfBatchPaused = useCallback(async () => {
    while (batchPausedRef.current) {
      await sleep(200);
    }
  }, []);

  const ensureSearchAllowance = useCallback(async (term, type) => {
    if (!isAuthenticated) {
      if (!allowGuestSearch) {
        throw new Error('Please log in to perform searches');
      }
      return;
    }
    if (recordSearch) {
      try {
        await recordSearch(term, type);
      } catch (err) {
        if (err && err.status === 429) throw err;
        console.warn('Search logging failed:', err);
      }
    }
    if (token) {
      adminApi
        .logActivity({ action: 'carrier_search', metadata: { term, type } }, token)
        .catch(() => {});
    }
  }, [allowGuestSearch, isAuthenticated, recordSearch, token]);

  const searchCarrierData = useCallback(async (searchTerm, searchType = 'auto', filters = {}) => {
    setLoading(true);
    setError(null);
    setCarrierData(null);
    setShowBasicInfo(true);

    try {
      const config = searchType === 'auto'
        ? apiService.determineSearchType(searchTerm)
        : { type: searchType, value: searchTerm };
      const typeForLog = (config && config.type) || searchType || 'auto';
      const valueForLog = (config && config.value) || searchTerm;
      await ensureSearchAllowance(valueForLog, typeForLog);
      const data = await apiService.search(searchTerm, searchType, filters);
      setCarrierData(data);
    } catch (err) {
      console.error('Search error:', err);
      if (err && (err.status === 429 || err.payload?.limit_type)) {
        setError(err.payload || {
          error: err.message,
          limit_type: 'search',
          status: 429,
          ...err
        });
      } else {
        setError(err.message || "An error occurred while searching");
      }
    } finally {
      setLoading(false);
    }
  }, [ensureSearchAllowance, apiService]);

  const clearBatchResults = useCallback(() => {
    setCarrierData(null);
    setError(null);
    setShowBasicInfo(true);
    setLoading(false);
    setIsBatchPaused(false);
    setIsBatchSearching(false);
    batchPausedRef.current = false;
  }, []);

  const batchSearch = useCallback(async (batchInput, filters = {}) => {
    setLoading(true);
    setError('');
    setIsBatchPaused(false);
    setIsBatchSearching(true);
    batchPausedRef.current = false;
    try {
      const searchTerms = batchInput
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (searchTerms.length === 0) throw new Error('Please enter at least one search term');

      const parseRange = (value) => {
        const match = value.match(/^\s*(\d+)\s*-\s*(\d+)\s*$/);
        if (!match) return null;
        const start = Number(match[1]);
        const end = Number(match[2]);
        if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
        return { start, end, width: match[1].length };
      };

      const getRangeCount = (value) => {
        const range = parseRange(value);
        if (!range) return 1;
        return range.end - range.start + 1;
      };

      const batchTotal = searchTerms.reduce((sum, term) => sum + getRangeCount(term), 0);

      let existing = null;
      setCarrierData(prev => {
        existing = prev && prev.searchType === 'batch' ? prev : null;
        return {
          searchType: 'batch',
          totalSearched: (existing ? existing.totalSearched : 0) + batchTotal,
          processed: existing ? existing.processed : 0,
          successful: existing ? existing.successful : 0,
          errorCount: existing ? (typeof existing.errorCount === 'number' ? existing.errorCount : (existing.errors || []).length) : 0,
          errors: existing ? [...(existing.errors || [])] : [],
          batchResults: existing ? [...(existing.batchResults || [])] : [],
          isComplete: false
        };
      });

      for (let i = 0; i < searchTerms.length; i++) {
        await waitIfBatchPaused();
        const term = searchTerms[i];
        const range = parseRange(term);
        const expectedCount = getRangeCount(term);
        try {
          if (range) {
            for (let n = range.start; n <= range.end; n++) {
              await waitIfBatchPaused();
              const mcValue = range.width ? String(n).padStart(range.width, '0') : String(n);
              try {
                await ensureSearchAllowance(mcValue, 'mc');
                const single = await apiService.searchSingleMC(mcValue);
                const filtered = apiService.applyFilters(single ? [single] : null, filters);
                const filteredArr = Array.isArray(filtered) ? filtered : filtered ? [filtered] : [];

                setCarrierData(prev => {
                  const prevState = prev || {
                    searchType: 'batch', totalSearched: batchTotal, processed: 0,
                    successful: 0, errorCount: 0, errors: [], batchResults: [], isComplete: false
                  };
                  return {
                    ...prevState,
                    processed: (prevState.processed || 0) + 1,
                    successful: (prevState.successful || 0) + (filteredArr.length ? 1 : 0),
                    errorCount: (prevState.errorCount || 0) + (filteredArr.length ? 0 : 1),
                    batchResults: filteredArr.length
                      ? [...(prevState.batchResults || []), ...filteredArr]
                      : (prevState.batchResults || [])
                  };
                });
              } catch (err) {
                setCarrierData(prev => ({
                  ...prev,
                  processed: (prev.processed || 0) + 1,
                  errorCount: (prev.errorCount || 0) + 1,
                  errors: [...(prev.errors || []), { term: mcValue, error: err.message }]
                }));
              }

              await sleep(50);
            }
            continue;
          }

          const detected = apiService.determineSearchType(term);
          let searchTypeToUse = 'auto';
          let searchValue = term;

          if (detected && typeof detected === 'object' && detected.type) {
            if (detected.type === 'range') {
              searchTypeToUse = 'auto';
              searchValue = term;
            } else if (['mc', 'dot', 'email', 'name'].includes(detected.type)) {
              searchTypeToUse = detected.type;
              searchValue = detected.value || term;
            }
          }

          await ensureSearchAllowance(searchValue, searchTypeToUse);
          const result = await apiService.search(searchValue, searchTypeToUse, filters);
          const successCount = Array.isArray(result) ? result.length : (result ? 1 : 0);
          const missingCount = Math.max(0, expectedCount - successCount);

          if (result) {
            setCarrierData(prev => {
              const prevState = prev || {
                searchType: 'batch', totalSearched: batchTotal, processed: 0,
                successful: 0, errorCount: 0, errors: [], batchResults: [], isComplete: false
              };
              return {
                ...prevState,
                processed: (prevState.processed || 0) + expectedCount,
                successful: (prevState.successful || 0) + successCount,
                errorCount: (prevState.errorCount || 0) + missingCount,
                batchResults: [
                  ...(prevState.batchResults || []),
                  ...(Array.isArray(result) ? result : [result])
                ]
              };
            });
          } else {
            setCarrierData(prev => ({
              ...prev,
              processed: (prev.processed || 0) + expectedCount,
              errorCount: (prev.errorCount || 0) + expectedCount,
              errors: expectedCount === 1
                ? [...(prev.errors || []), { term, error: 'Not Found' }]
                : [...(prev.errors || [])]
            }));
          }
        } catch (err) {
          setCarrierData(prev => ({
            ...prev,
            processed: (prev.processed || 0) + expectedCount,
            errorCount: (prev.errorCount || 0) + expectedCount,
            errors: [...(prev.errors || []), { term, error: err.message }]
          }));
        }

        await sleep(50);
      }

      setCarrierData(prev => ({ ...prev, isComplete: true }));
    } catch (err) {
      setError(err.message || 'Batch search failed');
      setCarrierData(null);
    } finally {
      batchPausedRef.current = false;
      setIsBatchPaused(false);
      setIsBatchSearching(false);
      setLoading(false);
    }
  }, [ensureSearchAllowance, apiService, waitIfBatchPaused]);

  const pauseBatchSearch = useCallback(() => {
    batchPausedRef.current = true;
    setIsBatchPaused(true);
  }, []);

  const resumeBatchSearch = useCallback(() => {
    batchPausedRef.current = false;
    setIsBatchPaused(false);
  }, []);

  const toggleInfoView = useCallback(() => {
    setShowBasicInfo(prev => !prev);
  }, []);

  return {
    carrierData,
    loading,
    error,
    searchCarrier: searchCarrierData,
    batchSearch,
    clearBatchResults,
    showBasicInfo,
    toggleInfoView,
    usageRemaining,
    isBatchPaused,
    isBatchSearching,
    pauseBatchSearch,
    resumeBatchSearch,
  };
};
